import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface RunWaypoint {
  id: string;
  run_id: string;
  name: string;
  description?: string;
  geom: any;
  waypoint_type?: string;
  symbol?: string;
  auto_generated: boolean;
  photo_urls: string[];
  detection_type?: string;
  ai_suggested_title?: string;
  recorded_at?: string;
  created_at: string;
}

export const useRunWaypoints = (runId: string | null) => {
  const [waypoints, setWaypoints] = useState<RunWaypoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setIsLoading(false);
      return;
    }

    fetchWaypoints();
  }, [runId]);

  const fetchWaypoints = async () => {
    if (!runId) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('run_waypoints')
        .select('*')
        .eq('run_id', runId)
        .order('recorded_at', { ascending: true, nullsFirst: false });

      if (fetchError) throw fetchError;

      // Map the data to ensure photo_urls is properly typed
      const mappedData = (data || []).map(wp => ({
        ...wp,
        photo_urls: Array.isArray(wp.photo_urls) ? wp.photo_urls as string[] : []
      }));

      setWaypoints(mappedData);
    } catch (err) {
      console.error('Error fetching waypoints:', err);
      setError(err instanceof Error ? err.message : 'Failed to load waypoints');
    } finally {
      setIsLoading(false);
    }
  };

  const saveWaypoints = async (updatedWaypoints: Partial<RunWaypoint>[], deletedIds?: string[]) => {
    if (!runId) return false;

    try {
      // Delete only explicitly removed waypoints (not all waypoints)
      if (deletedIds && deletedIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('run_waypoints')
          .delete()
          .in('id', deletedIds);

        if (deleteError) throw deleteError;
      }

      // Separate waypoints into updates (existing IDs) and inserts (temp IDs)
      const waypointsToUpdate = updatedWaypoints.filter(wp => wp.id && !wp.id.startsWith('temp-'));
      const waypointsToInsert = updatedWaypoints.filter(wp => !wp.id || wp.id.startsWith('temp-'));

      // Update existing waypoints
      for (const wp of waypointsToUpdate) {
        const { id, created_at, waypoint_type, ...updateData } = wp;
        const { error: updateError } = await supabase
          .from('run_waypoints')
          .update({
            ...updateData,
            run_id: runId,
          })
          .eq('id', id!);

        if (updateError) throw updateError;
      }

      // Insert new waypoints
      if (waypointsToInsert.length > 0) {
        const insertData = waypointsToInsert.map((wp) => {
          const { id, created_at, waypoint_type, ...rest } = wp;
          return {
            ...rest,
            run_id: runId,
            geom: rest.geom || null,
          };
        });

        const { error: insertError } = await supabase
          .from('run_waypoints')
          .insert(insertData as any);

        if (insertError) throw insertError;
      }

      await fetchWaypoints();
      return true;
    } catch (err) {
      console.error('Error saving waypoints:', err);
      setError(err instanceof Error ? err.message : 'Failed to save waypoints');
      return false;
    }
  };

  return {
    waypoints,
    isLoading,
    error,
    refetch: fetchWaypoints,
    saveWaypoints,
  };
};
